import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiGetJson, apiPostJson } from "../../utils/apiClient";
import { finishedManualRun, ManualRunWatch, pickFinishedWatch, stuckWatchIndexes, watchTarget } from "../../utils/rmsManualStatus";

// RN-279: 押してからこの時間たっても終わった記録が無ければ「途中で止まった可能性」を知らせる
//   取得の仕組み（rieki-navi-job-download）はリポジトリの設定で 1回 10分・最大3回やり直し（deploy/run-jobs/rieki-navi-job-download-prod.yaml）。
//   打ち切られると記録は running のまま残るため、10分×4回＋起動の待ちを見込んで 60分
const STUCK_LIMIT_MS = 60 * 60 * 1000;

// RN-279: 終わったときに出す知らせ（画面を暗くして真ん中に出す。閉じるまで残す。いくつか重なったら順に1つずつ）
type ResultNotice = {
    kind: "success" | "error" | "stuck";
    title: string;
    lines: string[];
};

const DEFAULT_TYPES = [
    "shop_data",
    "shop_sku_data",
    "item_data",
    "sales_hourly",
    "rmail",
    "afl",
    "ec",
    "rpp",
    "rpp_item",
    "rppexp",
    "rppexp_item",
    "cpa",
    "cpnadv",
    "cpnadv_item",
    "tda",
    "tdaexp",
    "tdaexp_item",
    "rpoint",
    "order"
];

export default function SuperRmsManualDownloadPage() {
    const [tenantId, setTenantId] = useState("");
    const [accountId, setAccountId] = useState("");
    const [shopUrl, setShopUrl] = useState("");

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [types, setTypes] = useState<string[]>(DEFAULT_TYPES);

    const [status, setStatus] = useState<any>(null);
    const [running, setRunning] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);

    // RN-279: 押した手動取得が終わったら、画面を暗くして真ん中に知らせを出す（閉じるまで残す）
    const [notices, setNotices] = useState<ResultNotice[]>([]);
    const pushNotice = useCallback((n: ResultNotice) => setNotices((prev) => [...prev, n]), []);
    const closeNotice = useCallback(() => setNotices((prev) => prev.slice(1)), []);
    const watchesRef = useRef<ManualRunWatch[]>([]);
    const pollSeqRef = useRef(0);

    const canPoll = useMemo(
        () => !!(tenantId.trim() && accountId.trim() && shopUrl.trim()),
        [tenantId, accountId, shopUrl]
    );

    const canRun = useMemo(() => {
        if (!tenantId.trim() || !accountId.trim() || !shopUrl.trim()) return false;
        if (!startDate.trim() || !endDate.trim()) return false;
        if (!Array.isArray(types) || types.length === 0) return false;
        return true;
    }, [tenantId, accountId, shopUrl, startDate, endDate, types]);

    const refreshStatus = useCallback(async () => {
        if (!canPoll) return;
        // 何番目に読み始めた記録か（押す前に読み始めた古い記録を、今回の結果にしないため）
        const pollSeq = ++pollSeqRef.current;
        try {
            const qs =
                `tenantId=${encodeURIComponent(tenantId.trim())}` +
                `&accountId=${encodeURIComponent(accountId.trim())}` +
                `&shopUrl=${encodeURIComponent(shopUrl.trim())}`;
            const data = await apiGetJson(`/api/super/rms-download/status?${qs}`);
            setStatus(data);

            const nowTarget = watchTarget(tenantId.trim(), accountId.trim(), shopUrl.trim());
            const idx = pickFinishedWatch(data, nowTarget, pollSeq, watchesRef.current);
            if (idx >= 0) {
                const w = watchesRef.current[idx];
                const done = finishedManualRun(data, w.sinceMs);
                const target = [`お店：${w.shopUrl}`, `期間：${w.startDate}〜${w.endDate}`];
                if (done === "success") {
                    watchesRef.current = watchesRef.current.filter((_, i) => i !== idx);
                    // 無料プランで、選んだ帳票が全部はずされたとき（何も取っていない）
                    const skipped = data?.lastResult?.skipped === true;
                    pushNotice({
                        kind: "success",
                        title: `手動データ取得が${w.errorNotified ? "、自動のやり直しで" : ""}完了しました`,
                        lines: [...target, ...(skipped ? ["※無料プランのため、取得する帳票がありませんでした"] : [])],
                    });
                } else {
                    // 取得は自動でやり直すことがある＝見張りは残し、あとで成功したらもう一度知らせる
                    watchesRef.current = watchesRef.current.map((x, i) => (i === idx ? { ...x, errorNotified: true } : x));
                    const reason = String(data?.errorMessage ?? "").slice(0, 200);
                    pushNotice({
                        kind: "error",
                        title: "手動データ取得が失敗しました",
                        lines: [
                            ...target,
                            ...(reason ? [`理由：${reason}`] : []),
                            "※自動でやり直すことがあります。やり直しで完了したときは、もう一度お知らせします",
                        ],
                    });
                }
            }

            // 終わった記録が無いまま時間が過ぎた回（途中で止まった可能性）。あとで終わったら、そのときも知らせる
            const stuck = stuckWatchIndexes(watchesRef.current, nowTarget, Date.now(), STUCK_LIMIT_MS);
            if (stuck.length > 0) {
                watchesRef.current = watchesRef.current.map((w, i) => (stuck.includes(i) ? { ...w, stuckNotified: true } : w));
                for (const i of stuck) {
                    const w = watchesRef.current[i];
                    pushNotice({
                        kind: "stuck",
                        title: `手動データ取得が、${Math.round(STUCK_LIMIT_MS / 60000)}分たっても終わっていません`,
                        lines: [
                            `お店：${w.shopUrl}`,
                            `期間：${w.startDate}〜${w.endDate}`,
                            "途中で止まった可能性があります。「実行履歴」で確かめてください",
                        ],
                    });
                }
            }
        } catch {
            // status 取得失敗時は黙殺（ポーリング停止しない）
        }
    }, [canPoll, tenantId, accountId, shopUrl, pushNotice]);

    useEffect(() => {
        let t: any = null;
        if (canPoll) {
            refreshStatus();
            t = setInterval(refreshStatus, 4000);
        }
        return () => t && clearInterval(t);
    }, [canPoll, refreshStatus]);

    const toggleType = useCallback((k: string) => {
        setTypes((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
    }, []);

    const selectAll = useCallback(() => setTypes(DEFAULT_TYPES), []);
    const clearAll = useCallback(() => setTypes([]), []);

    const run = useCallback(async () => {
        setMsg(null);
        setRunning(true);
        try {
            const body = {
                tenantId: tenantId.trim(),
                accountId: accountId.trim(),
                shopUrl: shopUrl.trim(),
                startDate: startDate.trim(),
                endDate: endDate.trim(),
                types,
            };
            // 押した時刻より後に終わった回だけを「今回の結果」とみなす（パソコンとサーバーの時計のずれを1分見込む）
            const pressedAtMs = Date.now();
            const sinceMs = pressedAtMs - 60_000;
            const res = await apiPostJson("/api/super/rms-download", body);
            watchesRef.current = [
                ...watchesRef.current,
                { sinceMs, pressedAtMs, target: watchTarget(body.tenantId, body.accountId, body.shopUrl), shopUrl: body.shopUrl, startDate: body.startDate, endDate: body.endDate, afterPollSeq: pollSeqRef.current },
            ];
            setMsg(`起動しました（manualKey=${res?.manualKey || "-"}）`);
            await refreshStatus();
        } catch (e: any) {
            setMsg(`エラー: ${String(e?.message || e)}`);
        } finally {
            setRunning(false);
        }
    }, [tenantId, accountId, shopUrl, startDate, endDate, types, refreshStatus]);

    // ✅ コンパクト共通class
    const page = "h-full flex flex-col max-w-5xl p-2 gap-2";
    const card = "bg-white border rounded-lg p-2";
    const label = "text-[11px] text-gray-600 leading-tight";
    const input = "w-full border rounded px-2 py-1 text-xs leading-tight";
    const btn =
        "text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50";
    const btnPrimary =
        "text-xs px-3 py-1.5 rounded border disabled:opacity-50 " +
        (running
            ? "bg-gray-200 text-gray-500 border-gray-200"
            : "bg-blue-600 text-white border-blue-700 hover:bg-blue-700");

    return (
        <div className={page}>
            {notices.length > 0 && (
                <ResultNoticeModal notice={notices[0]} remaining={notices.length - 1} onClose={closeNotice} />
            )}

            {/* ===== タイトル ===== */}
            <div className="shrink-0">
                <h1 className="text-base font-semibold leading-tight">RMS 手動データ取得（期間指定）</h1>
                <div className="text-[11px] text-gray-500 leading-tight">
                    tenant/account/shopUrl + 期間 + 種別を指定して手動起動
                </div>
            </div>

            {/* ===== 操作フォーム ===== */}
            <div className={`${card} shrink-0 space-y-2`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input label="tenantId" value={tenantId} onChange={setTenantId} disabled={running} />
                    <Input label="shopUrl" value={shopUrl} onChange={setShopUrl} disabled={running} />
                    <Input label="accountId" value={accountId} onChange={setAccountId} disabled={running} />
                </div>

                <div className="flex flex-col md:flex-row md:items-end gap-2">
                    <Input label="startDate (YYYY-MM-DD)" type="date" value={startDate} onChange={setStartDate} disabled={running} />
                    <Input label="endDate (YYYY-MM-DD)" type="date" value={endDate} onChange={setEndDate} disabled={running} />
                </div>

                <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <div className={`${label} font-semibold`}>取得するデータ</div>
                        <div className="flex items-center gap-2">
                            <button type="button" className={btn} onClick={selectAll} disabled={running}>
                                全選択
                            </button>
                            <button type="button" className={btn} onClick={clearAll} disabled={running}>
                                全解除
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1">
                        {DEFAULT_TYPES.map((k) => (
                            <label key={k} className="flex items-center gap-2 text-[12px] leading-tight">
                                <input
                                    type="checkbox"
                                    checked={types.includes(k)}
                                    onChange={() => toggleType(k)}
                                    disabled={running}
                                />
                                <span className="truncate" title={k}>
                                    {k}
                                </span>
                            </label>
                        ))}
                    </div>

                    <div className="mt-1 text-[11px] text-gray-500">
                        選択: <span className="font-semibold">{types.length}</span> / {DEFAULT_TYPES.length}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={run}
                        disabled={running || !canRun}
                        className={btnPrimary}
                        title={!canRun ? "tenant/account/shopUrl + start/end + types を指定してください" : ""}
                    >
                        {running ? "起動中..." : "この条件で取得開始"}
                    </button>

                    <button onClick={refreshStatus} disabled={!canPoll || running} className={btn}>
                        ステータス更新
                    </button>

                    {msg && <div className="text-xs text-gray-700 truncate" title={msg}>{msg}</div>}
                </div>
            </div>

            {/* ===== ステータス ===== */}
            <div className={`${card} flex-1 min-h-0 flex flex-col`}>
                <div className="flex items-center justify-between mb-1 shrink-0">
                    <div className="text-xs font-semibold">ステータス</div>
                    <div className="text-[11px] text-gray-500">
                        polling: {canPoll ? "ON" : "OFF"}（4s）
                    </div>
                </div>

                <pre className="flex-1 min-h-0 text-[11px] leading-snug bg-gray-50 border rounded p-2 overflow-auto">
                    {JSON.stringify(status, null, 2)}
                </pre>
            </div>
        </div>
    );
}

/* ===== 小部品 ===== */

// RN-279: 終わったときの知らせ（画面を暗くして真ん中に出す。見た目は RmsManualPage.tsx のお知らせにそろえる）
function ResultNoticeModal({ notice, remaining, onClose }: { notice: ResultNotice; remaining: number; onClose: () => void }) {
    const head =
        notice.kind === "success"
            ? { icon: "✓", bar: "bg-emerald-600", btn: "bg-emerald-600 hover:bg-emerald-700" }
            : notice.kind === "error"
              ? { icon: "!", bar: "bg-red-600", btn: "bg-red-600 hover:bg-red-700" }
              : { icon: "!", bar: "bg-amber-500", btn: "bg-amber-500 hover:bg-amber-600" };
    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-[460px] overflow-hidden rounded-xl bg-white shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
                <div className={`flex items-center gap-2 px-6 py-4 text-white ${head.bar}`}>
                    <span aria-hidden="true" className="flex h-6 w-6 items-center justify-center rounded-full bg-white/25 text-[14px] font-bold">
                        {head.icon}
                    </span>
                    <div className="text-[16px] font-semibold leading-[1.5]">{notice.title}</div>
                </div>
                <div className="px-6 py-5">
                    {notice.lines.map((line, i) => (
                        <p key={i} className={`break-words text-[13px] leading-[1.8] ${line.startsWith("※") ? "text-gray-500" : "text-[#333]"}`}>
                            {line}
                        </p>
                    ))}
                    <div className="mt-5 flex items-center justify-between border-t border-[#eee] pt-4">
                        <span className="text-[11px] text-gray-400">{remaining > 0 ? `ほかに ${remaining} 件のお知らせがあります` : ""}</span>
                        <button type="button" onClick={onClose} className={`rounded-lg px-6 py-2 text-[14px] font-semibold text-white transition ${head.btn}`}>
                            閉じる
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
function Input({
    label,
    value,
    onChange,
    disabled,
    type, // ✅ 追加（date対応）
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    disabled?: boolean;
    type?: React.HTMLInputTypeAttribute;
}) {
    const isDate = type === "date";
    return (
        <div>
            <div className="text-[11px] text-gray-600 leading-tight mb-1">{label}</div>
            <input
                className={
                    (isDate ? "w-full md:w-[220px]" : "w-full") + // ✅ dateだけ横幅を抑える
                    " border rounded px-2 py-1 text-xs leading-tight disabled:bg-gray-50 disabled:text-gray-500"
                }
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                type={type || "text"} // ✅ 追加
            />
        </div>
    );
}
